import { QueryClient } from '@tanstack/react-query'

/**
 * Default query client configuration.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry failed requests once before showing error
      retry: 1,
      // Consider data stale after 30 seconds
      staleTime: 30 * 1000,
      // Don't refetch on window focus for forms/detail pages
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Don't retry mutations by default
      retry: false,
    },
  },
})

/**
 * Query key factory for consistent key generation.
 * Use this to ensure consistent cache invalidation.
 */
export const queryKeys = {
  // Organizations
  organizations: {
    all: ['organizations'] as const,
    detail: (id: string) => ['organizations', id] as const,
  },

  // Buildings
  buildings: {
    all: (orgId: string) => ['buildings', { orgId }] as const,
    detail: (id: string) => ['buildings', id] as const,
  },

  // Rooms
  rooms: {
    all: (buildingId: string) => ['rooms', { buildingId }] as const,
    detail: (id: string) => ['rooms', id] as const,
  },

  // Assets
  assets: {
    all: (orgId: string) => ['assets', { orgId }] as const,
    detail: (id: string) => ['assets', id] as const,
    byRoom: (roomId: string) => ['assets', { roomId }] as const,
  },

  // Reports
  reports: {
    all: (filters?: { orgId?: string; buildingId?: string; roomId?: string }) =>
      ['reports', filters] as const,
    detail: (id: string) => ['reports', id] as const,
  },

  // Users
  users: {
    all: (orgId: string) => ['users', { orgId }] as const,
    detail: (id: string) => ['users', id] as const,
    me: () => ['users', 'me'] as const,
  },
}

/**
 * Invalidation helpers for common patterns.
 */
export const invalidateQueries = {
  organizations: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all })
  },

  buildings: (orgId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.buildings.all(orgId) })
  },

  rooms: (buildingId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all(buildingId) })
  },

  assets: (orgId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.assets.all(orgId) })
  },

  reports: () => {
    queryClient.invalidateQueries({ queryKey: ['reports'] })
  },

  users: (orgId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.users.all(orgId) })
  },
}
